import { z } from "zod";
import { resolveEffectiveUnitDiscount } from "@/modules/cf-feed/domain/lib/cf-session-pricing";

const money = z.number().int().min(0).max(999999999);
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const displayAppearanceSchema = z.object({
  canvasColor: color.default("#10151d"),
  cardColor: color.default("#0d141d"),
  textColor: color.default("#ffffff"),
  mutedColor: color.default("#c5cedb"),
  codeColor: color.default("#77e7ba"),
  priceColor: color.default("#ffffff"),
  borderColor: color.default("#354154"),
  opacity: z.number().int().min(0).max(100).default(96),
  width: z.number().int().min(35).max(100).default(100),
  margin: z.number().min(0).max(6).default(3),
  padding: z.number().min(1).max(4).default(3),
  radius: z.number().min(0).max(4).default(1.5),
  borderWidth: z.number().int().min(0).max(8).default(0),
  fontScale: z.number().int().min(70).max(125).default(100),
  fontFamily: z.enum(["app", "sans", "serif"]).default("app"),
  imageScale: z.number().int().min(50).max(140).default(100),
  imageFit: z.enum(["contain", "cover"]).default("contain"),
  horizontal: z.enum(["left", "center", "right"]).default("center"),
  vertical: z.enum(["top", "center", "bottom"]).default("bottom"),
  shadow: z.boolean().default(false),
  variantsPerPage: z.number().int().min(1).max(5).default(4),
  pageSeconds: z.number().int().min(3).max(30).default(6),
});
export type DisplayAppearance = z.infer<typeof displayAppearanceSchema>;
export const defaultDisplayAppearance = displayAppearanceSchema.parse({});
export const displaySettingsSchema = z.object({
  appearance: displayAppearanceSchema.default(defaultDisplayAppearance),
  image: z.boolean().default(true),
  title: z.boolean().default(true),
  codes: z.boolean().default(true),
  prices: z.boolean().default(true),
  remaining: z.boolean().default(true),
  orders: z.boolean().default(true),
  buyerName: z.enum(["hidden", "short", "full"]).default("hidden"),
  totals: z.boolean().default(false),
  next: z.boolean().default(false),
  orderMode: z.enum(["accepted", "paid"]).default("accepted"),
  background: z.enum(["transparent", "solid"]).default("transparent"),
  orientation: z.enum(["landscape", "portrait"]).default("landscape"),
});
export type DisplaySettings = z.infer<typeof displaySettingsSchema>;
export const defaultDisplaySettings = displaySettingsSchema.parse({});
export const optionSchema = z.object({
  adminOptionDefinitionId: z.string().min(1),
  label: z.string().default(""),
  surchargeLak: money,
  isAutoAttach: z.boolean().default(true),
  requiresCustomerInput: z.boolean().default(false),
});
export type WorkspaceOption = z.infer<typeof optionSchema>;
export const promoSchema = z.object({
  listDiscountPercent: z
    .number()
    .min(0)
    .max(100)
    .multipleOf(0.01)
    .nullable()
    .default(null),
  listDiscountFixedLak: money.multipleOf(1000).nullable().default(null),
  freeShipping: z.boolean().default(false),
  shippingFeeLak: money.nullable().default(null),
  options: z.array(optionSchema).max(30).default([]),
});
export type WorkspacePromo = z.infer<typeof promoSchema>;
export const priceInputSchema = z.object({
  mode: z.enum(["inherit", "none", "sale", "percent", "fixed"]),
  value: z.number().min(0).max(999999999).optional(),
});
export type PriceInput = z.infer<typeof priceInputSchema>;
export function resolvePriceInput(base: number, input: PriceInput) {
  if (input.mode === "inherit")
    return { discountPercent: null, discountFixedLak: null };
  if (input.mode === "none")
    return { discountPercent: 0, discountFixedLak: null };
  if (input.value == null || !Number.isFinite(input.value) || input.value < 0)
    throw new Error("INVALID_PRICE");
  if (
    input.mode === "percent" &&
    (input.value > 100 ||
      Math.abs(input.value * 100 - Math.round(input.value * 100)) > 0.00001)
  )
    throw new Error("INVALID_DISCOUNT");
  const discountPercent = input.mode === "percent" ? input.value : null;
  const discountFixedLak =
    input.mode === "sale"
      ? base - input.value
      : input.mode === "fixed"
        ? input.value
        : null;
  if (
    discountFixedLak != null &&
    (discountFixedLak < 0 || discountFixedLak % 1000 !== 0)
  )
    throw new Error("PRICE_NOT_REPRESENTABLE");
  return { discountPercent, discountFixedLak };
}
export function priceForWorkspace(
  base: number,
  product: { discountPercent: number | null; discountFixedLak: number | null },
  promo: WorkspacePromo,
) {
  return resolveEffectiveUnitDiscount({
    productUnitPriceLak: base,
    product,
    list: promo,
  }).unitAfterDiscountLak;
}
export function moveProduct(
  ids: string[],
  id: string,
  position: number,
): string[] {
  if (
    !ids.includes(id) ||
    !Number.isInteger(position) ||
    position < 1 ||
    position > ids.length
  )
    throw new Error("INVALID_POSITION");
  const next = ids.filter((value) => value !== id);
  next.splice(position - 1, 0, id);
  return next;
}
export const workspaceCommandSchema = z.object({
  key: z.string().min(8).max(100),
  revision: z.number().int().nonnegative(),
  action: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("move"),
      productId: z.string(),
      position: z.number().int().min(1),
    }),
    z.object({ type: z.literal("display"), productId: z.string().nullable() }),
    z.object({ type: z.literal("next") }),
    z.object({ type: z.literal("skip") }),
    z.object({
      type: z.literal("exclude"),
      productId: z.string(),
      excluded: z.boolean(),
    }),
    z.object({ type: z.literal("settings"), settings: displaySettingsSchema }),
    z.object({
      type: z.literal("token"),
      token: z.string().regex(/^[a-f0-9]{64}$/),
    }),
    z.object({
      type: z.literal("add"),
      variantIds: z.array(z.string()).min(1).max(100),
      warehouseId: z.string().optional(),
      price: priceInputSchema.optional(),
      options: z.array(optionSchema).max(30).nullable().optional(),
    }),
    z.object({
      type: z.literal("apply-product-set"),
      templateId: z.string().min(1),
    }),
    z.object({
      type: z.literal("copy"),
      title: z.string().trim().min(1).max(255),
    }),
    z.object({ type: z.literal("revoke") }),
    z.object({ type: z.literal("promo"), promo: promoSchema }),
    z.object({
      type: z.literal("details"),
      title: z.string().trim().min(1).max(255),
      description: z.string().max(10000).nullable(),
      warehouseId: z.string().optional(),
    }),
    z.object({ type: z.literal("ordering"), enabled: z.boolean() }),
    z.object({
      type: z.literal("remove-products"),
      ids: z.array(z.string()).min(1).max(500),
    }),
    z.object({
      type: z.literal("products"),
      ids: z.array(z.string()).min(1).max(500),
      price: priceInputSchema.optional(),
      options: z.array(optionSchema).max(30).nullable().optional(),
      saleEnabled: z.boolean().optional(),
      allocatedQuantity: z.number().int().min(1).optional(),
      cfCode: z.string().min(1).max(10).optional(),
    }),
  ]),
});
export type WorkspaceCommand = z.infer<typeof workspaceCommandSchema>;
export type WorkspacePreview = {
  revision: number;
  items: Array<{
    id: string;
    cfCode: string;
    before: number;
    after: number;
    options: number;
    error?: string;
  }>;
};
export type WorkspaceVariant = {
  id: string;
  variantId: string;
  groupId: string;
  name: string;
  label: string;
  sku: string | null;
  barcode?: string | null;
  cfCode: string;
  imageUrl: string | null;
  price: number;
  salePrice: number;
  discountPercent: number | null;
  discountFixedLak: number | null;
  sortOrder: number;
  saleEnabled: boolean;
  allocatedQuantity: number | null;
  allocationMode: "limit" | "unlimited";
  soldQuantity: number;
  stockRemaining: number;
  remaining: number | null;
  isPreorder: boolean;
  canSell: boolean;
  options: WorkspaceOption[];
  optionsInherited: boolean;
};
export type WorkspaceSnapshot = {
  feedType: "post" | "live" | null;
  id: string;
  revision: number;
  draftId: string | null;
  feedId: string | null;
  pageId: string;
  title: string;
  description: string | null;
  warehouseId: string | null;
  ordering: boolean;
  tokenActive: boolean;
  state: DisplayState;
  promo: WorkspacePromo;
  products: WorkspaceVariant[];
  queue: Array<{ id: string; title: string }>;
  total: number;
  preview: PublicDisplay;
};
export type DisplayState = {
  activeProductId: string | null;
  excludedProductIds: string[];
  settings: DisplaySettings;
};
export const defaultDisplayState: DisplayState = {
  activeProductId: null,
  excludedProductIds: [],
  settings: defaultDisplaySettings,
};
export type DisplayProduct = {
  id: string;
  title?: string;
  imageUrl?: string | null;
  variants: Array<{
    id: string;
    label: string;
    cfCode?: string;
    price?: number;
    salePrice?: number;
    remaining?: number | null;
  }>;
};
export type DisplayOrder = {
  reference: string;
  orderCode?: string | null;
  cfCode: string;
  quantity: number;
  name?: string;
  total?: number;
  isPaid?: boolean;
};
export type PublicDisplay = {
  settings: DisplaySettings;
  product: DisplayProduct | null;
  next: Array<{ id: string; title: string }>;
  orders: DisplayOrder[];
  totals?: { claimQuantity: number; claimValue: number };
};
/** ส่งเฉพาะ whitelist และตัดข้อมูลที่ปิดตั้งแต่ server */
export function projectPublicDisplay(input: {
  settings: DisplaySettings;
  hidden: boolean;
  product: DisplayProduct | null;
  next: Array<{ id: string; title: string }>;
  orders: DisplayOrder[];
  totals: { claimQuantity: number; claimValue: number };
}): PublicDisplay {
  const s = input.settings;
  const p = input.product;
  return {
    settings: s,
    product:
      !input.hidden && p
        ? {
            id: p.id,
            ...(s.title ? { title: p.title } : {}),
            ...(s.image ? { imageUrl: p.imageUrl } : {}),
            variants: p.variants.map((v) => ({
              id: v.id,
              label: v.label,
              ...(s.codes ? { cfCode: v.cfCode } : {}),
              ...(s.prices ? { price: v.price, salePrice: v.salePrice } : {}),
              ...(s.remaining ? { remaining: v.remaining } : {}),
            })),
          }
        : null,
    next: !input.hidden && s.next ? input.next : [],
    orders:
      !input.hidden && s.orders
        ? input.orders
            .filter((o) => s.orderMode !== "paid" || o.isPaid)
            .slice(0, 5)
            .map((o) => ({
              reference: o.reference,
              ...(o.orderCode ? { orderCode: o.orderCode } : {}),
              cfCode: o.cfCode,
              quantity: o.quantity,
              ...(s.buyerName === "full"
                ? { name: o.name }
                : s.buyerName === "short"
                  ? {
                      name: `${Array.from(o.name ?? "")
                        .slice(0, 3)
                        .join("")}…`,
                    }
                  : {}),
            }))
        : [],
    ...(!input.hidden && s.totals ? { totals: input.totals } : {}),
  };
}
